import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Typography, styled, Paper, TextField, MenuItem, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { trackLead } from '../../utils/analytics';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundImage: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url("/assets/images/hero-bg.jpg")',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  color: '#fff',
  padding: theme.spacing(15, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(10, 0),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100px',
    background: 'linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))',
    zIndex: 1,
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

const TrustBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(0.5, 2),
  borderRadius: '50px',
  marginBottom: theme.spacing(3),
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Phone number constant
const PHONE_NUMBER = '9202558952';
const PHONE_FORMATTED = '(920) 255-8952';

// Service types for quick form
const serviceTypes = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'junk-removal', label: 'Junk Removal' },
  { value: 'moving', label: 'Moving' },
  { value: 'specialty', label: 'Other' }
];

interface HeroProps {
  title?: string;
  subtitle?: string;
  showPhone?: boolean;
  showQuickForm?: boolean;
  backgroundImage?: string;
  ctaText?: string;
  ctaLink?: string;
  // Preselects the quick form's service, e.g. 'junk-removal'
  defaultService?: string;
}

const Hero: React.FC<HeroProps> = ({
  title = "Professional Delivery, Junk Removal & Moving Services",
  subtitle = "Fast, reliable and affordable solutions for Northeast Wisconsin. Years of experience, transparent pricing, fully insured, and customer focused.",
  showPhone = true,
  showQuickForm = true,
  backgroundImage,
  ctaText = "Get a Quote Now",
  ctaLink = "/contact",
  defaultService = ''
}) => {
  // Quick form state
  const [quickFormData, setQuickFormData] = useState({ name: '', phone: '', service: defaultService });
  const [quickFormSubmitted, setQuickFormSubmitted] = useState(false);
  const [quickFormSubmitting, setQuickFormSubmitting] = useState(false);
  const [quickFormErrors, setQuickFormErrors] = useState<{[key: string]: string}>({});

  const handleQuickFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuickFormData({ ...quickFormData, [e.target.name]: e.target.value });
    // Clear error when user types
    if (quickFormErrors[e.target.name]) {
      setQuickFormErrors({ ...quickFormErrors, [e.target.name]: '' });
    }
  };

  const handleQuickFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: {[key: string]: string} = {};
    if (!quickFormData.name.trim()) errors.name = 'Required';
    if (!quickFormData.phone.trim()) errors.phone = 'Required';
    if (!quickFormData.service) errors.service = 'Required';

    if (Object.keys(errors).length > 0) {
      setQuickFormErrors(errors);
      return;
    }

    setQuickFormSubmitting(true);
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'quick-quote',
          ...quickFormData
        }).toString()
      });

      if (response.ok) {
        setQuickFormSubmitted(true);
        trackLead('quick-quote', quickFormData.service);
      } else {
        setQuickFormErrors({ submit: 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setQuickFormErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setQuickFormSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.5,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <HeroSection
      sx={backgroundImage ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${backgroundImage})` } : {}}
    >
      <Container>
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent={{ xs: 'center', md: 'space-between' }}
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <ContentBox maxWidth={{ md: '60%' }}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <TrustBadge>
                  <Typography variant="body2">Trusted Service Provider Since 2022</Typography>
                </TrustBadge>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {title}
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ 
                    mb: 4, 
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.9)',
                  }}
                >
                  {subtitle}
                </Typography>
              </motion.div>

              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <motion.div
                  variants={itemVariants}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    component={RouterLink}
                    to={ctaLink}
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      fontWeight: 700,
                      fontSize: '1.1rem',
                    }}
                  >
                    {ctaText}
                  </Button>

                  {showPhone && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="large"
                      startIcon={<PhoneIcon />}
                      href={`tel:${PHONE_NUMBER}`}
                      sx={{ 
                        py: 1.5, 
                        px: 3, 
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: '#fff',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      {PHONE_FORMATTED}
                    </Button>
                  )}
                </motion.div>
              </Box>
            </motion.div>
          </ContentBox>

          {showQuickForm && (
            <Box 
              sx={{ 
                width: { xs: '100%', md: '35%' }, 
                mt: { xs: 6, md: 0 },
                maxWidth: { xs: '400px', md: 'none' },
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Paper 
                  elevation={4}
                  sx={{ 
                    p: 3, 
                    backgroundColor: 'rgba(255,255,255,0.97)',
                    borderRadius: 2,
                  }}
                >
                  {!quickFormSubmitted ? (
                    <>
                      <Typography 
                        variant="h6" 
                        component="h3" 
                        gutterBottom 
                        fontWeight={700}
                        color="text.primary"
                        sx={{ mb: 2 }}
                      >
                        Get a Quick Quote
                      </Typography>
                      <form
                        name="quick-quote"
                        method="POST"
                        data-netlify="true"
                        data-netlify-honeypot="bot-field"
                        onSubmit={handleQuickFormSubmit}
                      >
                        <input type="hidden" name="form-name" value="quick-quote" />
                        <p hidden>
                          <label>
                            Don't fill this out: <input name="bot-field" />
                          </label>
                        </p>
                        {quickFormErrors.submit && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {quickFormErrors.submit}
                          </Alert>
                        )}
                        <TextField
                          fullWidth
                          size="small"
                          name="name"
                          label="Your Name"
                          value={quickFormData.name}
                          onChange={handleQuickFormChange}
                          error={!!quickFormErrors.name}
                          helperText={quickFormErrors.name}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          name="phone"
                          label="Phone Number"
                          value={quickFormData.phone}
                          onChange={handleQuickFormChange}
                          error={!!quickFormErrors.phone}
                          helperText={quickFormErrors.phone}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          select
                          name="service"
                          label="Service Needed"
                          value={quickFormData.service}
                          onChange={handleQuickFormChange}
                          error={!!quickFormErrors.service}
                          helperText={quickFormErrors.service}
                          sx={{ mb: 2 }}
                        >
                          {serviceTypes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={quickFormSubmitting}
                          startIcon={<SendIcon />}
                          sx={{ fontWeight: 600 }}
                        >
                          {quickFormSubmitting ? 'Sending...' : 'Get Quote'}
                        </Button>
                      </form>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ display: 'block', mt: 2, textAlign: 'center' }}
                      >
                        We'll call you back shortly.
                      </Typography>
                    </>
                  ) : (
                    <Box textAlign="center" py={2}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h6" color="text.primary" gutterBottom>
                        Request Received!
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We'll call you back shortly.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </motion.div>
            </Box>
          )}
        </Box>
      </Container>
    </HeroSection>
  );
};

export default Hero;
